import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { ExternalLink } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link } from 'router-v6'
import { useFlowStore } from 'v2/pages/flow/store'
import { ORGANIZATION_SETTINGS_TABS } from 'v2/shared/components/user-avatar-popover-sections'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { HandoffConnection } from 'v2/types/api/handoff-context'
import { errorToast, successToast } from 'v2/utils/toasts'
import { ConnectionSteps } from './handoff-connection-steps'
import { integrationInstructions, uploadUrl } from './handoff-context.helpers'

const adminError = (title: string, action: string) => (error: unknown) =>
  errorToast({
    title,
    description:
      (error as AxiosError).response?.status === 403
        ? `Only admins can ${action}.`
        : getErrorText(error as AxiosError<ApiResponse<unknown>>),
  })

const copy = (text: string, what: string) =>
  navigator.clipboard.writeText(text).then(
    () => successToast({ title: `${what} copied` }),
    () => errorToast({ title: `${what} not copied`, description: 'Select and copy it manually.' }),
  )

interface ConnectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  connection: HandoffConnection
}

const SettingsLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} target="_blank" className="inline-flex items-center gap-1 underline">
    {children} <ExternalLink className="size-3.5" />
  </Link>
)

/** The organization's AI agent: the name human specialists see, its credentials, the endpoint and the steps. */
export const ConnectionDialog = ({ open, onOpenChange, connection }: ConnectionDialogProps) => {
  const url = uploadUrl(resolveBaseUrl())
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-xl grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden"
        data-testid="qnr-context-connection"
      >
        <DialogHeader>
          <DialogTitle>Connect your AI agent</DialogTitle>
          <DialogDescription>Shared by every Flow in your organization.</DialogDescription>
        </DialogHeader>
        <NameField connection={connection} />
        <div className="flex flex-col gap-1.5" data-testid="qnr-context-credentials">
          <Label>Credentials</Label>
          <span className="text-s-400 text-secondary">
            Your AI agent uses a Plivo Auth ID and Auth Token. Create a sub-account for it in{' '}
            <SettingsLink to={ORGANIZATION_SETTINGS_TABS.subAccounts}>Sub-accounts</SettingsLink>,
            or use your own from{' '}
            <SettingsLink to={ORGANIZATION_SETTINGS_TABS.apiKeys}>API keys</SettingsLink>. If{' '}
            <SettingsLink to={ORGANIZATION_SETTINGS_TABS.ipWhitelisting}>
              IP allowlisting
            </SettingsLink>{' '}
            is on, add your AI agent&apos;s server IPs.
          </span>
        </div>
        <Input
          label="Endpoint"
          value={url}
          readOnly
          inputClassName="font-jetbrains pr-10"
          endIcon={
            <ClipboardCopyButton textToCopy={url} aria-label="Copy endpoint" tooltipText="Copy" />
          }
          endIconClassName="pr-3"
        />
        <ConnectionSteps url={url} />
        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => copy(integrationInstructions(url), 'Setup steps')}
          >
            Copy all steps
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const CONNECTION_QUERY_KEY = conversationQueryKeys().handoffContextConnection

const NameField = ({ connection }: { connection: HandoffConnection }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const queryClient = useQueryClient()
  const [name, setName] = useState<string>()
  const draft = name ?? connection.name
  const rename = useMutation({
    mutationFn: async (value: string) => (await handoffContextService.renameConnection(value)).data,
    onSuccess: () => {
      setName(undefined)
      return queryClient.invalidateQueries({ queryKey: CONNECTION_QUERY_KEY })
    },
    onError: adminError('Name not saved', 'change the name'),
  })
  const changed = !!draft.trim() && draft.trim() !== connection.name

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Input
          label="Name shown to human specialists"
          value={draft}
          readOnly={!connection.is_admin}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {connection.is_admin && changed && (
        <Button
          type="button"
          variant="outline"
          disabled={!isEditing || rename.isPending}
          onClick={() => rename.mutate(draft.trim())}
        >
          Save
        </Button>
      )}
    </div>
  )
}
